import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Deposant, RendezVous, StatutRdv } from '@troc/shared'
import {
  avertissementsRdv,
  estFeteNationaleBelge,
  estMatin,
  feteDuJour,
  horaires,
  JOURS_DEPOT,
  libelleHoraires,
  LIBELLE_STATUT_RDV,
  PIECES_APRES_MIDI_MAX,
  PIECES_MATIN_MAX,
  PIECES_MATIN_MIN,
} from '@troc/shared'
import { api, ErreurApi } from '../api'
import { Ecran } from '../composants/Ecran'
import { useToast } from '../etat/Toast'
import { useReveil } from '../composants/Volets'

/**
 * L'agenda des rendez-vous de dépôt.
 *
 * On y prend un rendez-vous en trois gestes : un jour, une heure, un nom. Le
 * nombre de pièces annoncé est facultatif mais c'est lui qui compte : c'est
 * ce qui décide si la matinée tient debout.
 *
 * L'écran avertit quand un créneau contredit les règles de la boutique, il ne
 * refuse jamais. Un client qui insiste, une exception qu'on accorde : c'est la
 * boutique qui tranche, pas le logiciel.
 */

const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi']
const FORMAT_HEURE = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' })

/** Les créneaux rapides : le matin de 10 h à 12 h, l'après-midi de 13 h 30 à 17 h 30. */
const CRENEAUX_RAPIDES = [
  '10:00', '10:30', '11:00', '11:30', '12:00',
  '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
]

/** Le lundi de la semaine contenant cette date. */
function lundiDe(date: Date): Date {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  const decalage = (d.getDay() + 6) % 7
  d.setDate(d.getDate() - decalage)
  return d
}

function jourIso(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`
}

/** « 2026-07-28T10:30:00.000Z » → « 10:30 », en heure locale. */
function heureDe(iso: string): string {
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/** « 2026-07-28 » + « 10:30 » → l'ISO correspondant en heure locale. */
function versIso(jour: string, heure: string): string {
  const [a, m, j] = jour.split('-').map(Number)
  const [h, min] = heure.split(':').map(Number)
  return new Date(a!, (m ?? 1) - 1, j, h ?? 0, min ?? 0, 0, 0).toISOString()
}

export function Agenda() {
  const naviguer = useNavigate()
  const { signaler, alerter } = useToast()
  const [semaine, setSemaine] = useState(() => lundiDe(new Date()))
  const [rdv, setRdv] = useState<RendezVous[]>([])
  const [notes, setNotes] = useState<Record<string, string>>({})
  const [ouvert, setOuvert] = useState<string | null>(null)
  const [heureOuverture, setHeureOuverture] = useState<string | undefined>(undefined)
  const [enEdition, setEnEdition] = useState<RendezVous | null>(null)
  // Un rendez-vous se lit d'abord par son heure et son nom ; le reste (tél.,
  // notes, actions) ne s'ouvre qu'à la demande — une journée chargée ne doit
  // pas devenir une colonne de pavés identiques.
  const [detailsOuverts, setDetailsOuverts] = useState<Set<number>>(new Set())
  const basculerDetail = (id: number) =>
    setDetailsOuverts((s) => {
      const suivant = new Set(s)
      if (suivant.has(id)) suivant.delete(id)
      else suivant.add(id)
      return suivant
    })
  const [abonnement, setAbonnement] = useState('')

  const fin = useMemo(() => {
    const d = new Date(semaine)
    d.setDate(d.getDate() + 7)
    return d
  }, [semaine])

  const charger = useCallback(() => {
    api<{ rendezVous: RendezVous[] }>(
      `/rendez-vous?du=${jourIso(semaine)}&au=${jourIso(fin)}`,
    )
      .then((r) => setRdv(r.rendezVous))
      .catch(() => undefined)
    api<{ notes: Record<string, string> }>(
      `/notes-jour?du=${jourIso(semaine)}&au=${jourIso(fin)}`,
    )
      .then((r) => setNotes(r.notes))
      .catch(() => undefined)
  }, [semaine, fin])

  useEffect(charger, [charger])
  useReveil('/agenda', charger)

  useEffect(() => {
    api<{ url: string }>('/agenda/abonnement')
      .then((r) => setAbonnement(r.url))
      .catch(() => undefined)
  }, [])

  const jours = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const d = new Date(semaine)
        d.setDate(d.getDate() + i)
        return d
      }),
    [semaine],
  )

  const parJour = (jour: Date) =>
    rdv
      .filter((r) => jourIso(new Date(r.debut)) === jourIso(jour))
      .sort((a, b) => a.debut.localeCompare(b.debut))

  const changerStatut = async (r: RendezVous, statut: StatutRdv) => {
    try {
      await api(`/rendez-vous/${r.id}`, { methode: 'PUT', corps: { statut } })
      charger()
    } catch (cause) {
      alerter(cause instanceof ErreurApi ? cause.message : 'Modification impossible.')
    }
  }

  const supprimer = async (r: RendezVous) => {
    if (!window.confirm(`Supprimer le rendez-vous de ${r.nom} ?`)) return
    try {
      await api(`/rendez-vous/${r.id}`, { methode: 'DELETE' })
      signaler('Rendez-vous supprimé.')
      charger()
    } catch (cause) {
      alerter(cause instanceof ErreurApi ? cause.message : 'Suppression impossible.')
    }
  }

  const aujourdhui = jourIso(new Date())
  const semaineCourante = jourIso(lundiDe(new Date())) === jourIso(semaine)

  return (
    <Ecran
      titre="Agenda"
      sousTitre="Rendez-vous de dépôt"
      outils={
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn-secondaire btn-icone"
            aria-label="Semaine précédente"
            onClick={() => {
              const d = new Date(semaine)
              d.setDate(d.getDate() - 7)
              setSemaine(d)
            }}
          >
            ‹
          </button>
          <button
            type="button"
            className="btn btn-secondaire"
            disabled={semaineCourante}
            onClick={() => setSemaine(lundiDe(new Date()))}
          >
            Cette semaine
          </button>
          <button
            type="button"
            className="btn btn-secondaire btn-icone"
            aria-label="Semaine suivante"
            onClick={() => {
              const d = new Date(semaine)
              d.setDate(d.getDate() + 7)
              setSemaine(d)
            }}
          >
            ›
          </button>
        </div>
      }
    >
      <div className="zone-defilante">
        <div className="semaine">
          {jours.map((jour) => {
            const cle = jourIso(jour)
            const ouvertAuDepot = horaires(jour) !== null
            const samedi = !JOURS_DEPOT.includes(jour.getDay() as (typeof JOURS_DEPOT)[number])
            const liste = parJour(jour)
            const piecesMatin = liste
              .filter((r) => r.statut !== 'annule' && estMatin(r.debut))
              .reduce((s, r) => s + r.nbPieces, 0)
            const piecesApresMidi = liste
              .filter((r) => r.statut !== 'annule' && !estMatin(r.debut))
              .reduce((s, r) => s + r.nbPieces, 0)

            return (
              <div
                key={cle}
                className={`jour${cle === aujourdhui ? ' jour-actuel' : ''}${
                  ouvertAuDepot ? '' : ' jour-ferme'
                }`}
              >
                <div className="jour-entete">
                  <span className="titre">{JOURS[jour.getDay()]}</span>
                  <span className="sourdine">{jour.getDate()}</span>
                </div>
                <div className="jour-fete sourdine">
                  {estFeteNationaleBelge(jour) ? 'Fête nationale · ' : ''}
                  {feteDuJour(jour)}
                </div>

                {ouvertAuDepot ? (
                  <div className="jour-horaires">
                    {libelleHoraires(jour)}
                    {samedi && <span className="sourdine"> · dépôt rare</span>}
                  </div>
                ) : (
                  <div className="jour-note">Fermé</div>
                )}

                {ouvertAuDepot && (piecesMatin > 0 || piecesApresMidi > 0) && (
                  <div className="jour-charge">
                    {piecesMatin > 0 && (
                      <span
                        className={piecesMatin > PIECES_MATIN_MAX ? 'charge alerte' : 'charge'}
                        title={`Matin : ${PIECES_MATIN_MIN} à ${PIECES_MATIN_MAX} pièces`}
                      >
                        matin {piecesMatin}
                      </span>
                    )}
                    {piecesApresMidi > 0 && (
                      <span
                        className={
                          piecesApresMidi > PIECES_APRES_MIDI_MAX ? 'charge alerte' : 'charge'
                        }
                        title={`Après-midi : ${PIECES_APRES_MIDI_MAX} pièces au plus`}
                      >
                        a-m {piecesApresMidi}
                      </span>
                    )}
                  </div>
                )}

                {liste.map((r, i) => {
                  // Le repère matin / après-midi ne s'affiche qu'à la bascule,
                  // une fois par colonne — pas devant chaque rendez-vous.
                  const matin = estMatin(r.debut)
                  const bascule = i === 0 || matin !== estMatin(liste[i - 1]!.debut)
                  const detailOuvert = detailsOuverts.has(r.id)

                  return (
                    <div key={r.id} style={{ display: 'contents' }}>
                      {bascule && (
                        <div className={`rdv-periode ${matin ? 'rdv-periode-matin' : 'rdv-periode-am'}`}>
                          {matin ? 'Matin' : 'Après-midi'}
                        </div>
                      )}
                      {enEdition?.id === r.id ? (
                        <NouveauRdv
                          jour={cle}
                          existant={r}
                          onAnnuler={() => setEnEdition(null)}
                          onCree={() => {
                            setEnEdition(null)
                            charger()
                            signaler('Rendez-vous modifié.')
                          }}
                          onErreur={alerter}
                        />
                      ) : (
                        <div
                          className={`rdv rdv-${r.statut} ${matin ? 'rdv-matin' : 'rdv-am'}`}
                        >
                          <button
                            type="button"
                            className="rdv-resume"
                            onClick={() => basculerDetail(r.id)}
                            aria-expanded={detailOuvert}
                          >
                            <span className="rdv-heure">
                              {FORMAT_HEURE.format(new Date(r.debut))}
                              {r.nbPieces > 0 && <span className="sourdine"> · {r.nbPieces} p.</span>}
                            </span>
                            <span className="rdv-nom">{r.nom}</span>
                            {r.statut !== 'prevu' && (
                              <span className="rdv-statut">{LIBELLE_STATUT_RDV[r.statut]}</span>
                            )}
                            <span className="rdv-chevron">{detailOuvert ? '▾' : '▸'}</span>
                          </button>

                          {detailOuvert && (
                            <div className="rdv-detail">
                              {r.deposantId && (
                                <button
                                  type="button"
                                  className="lien-nu"
                                  onClick={() => naviguer(`/deposants/${r.deposantId}`)}
                                >
                                  Voir la fiche client
                                </button>
                              )}
                              {r.tel && <div className="sourdine rdv-tel">{r.tel}</div>}
                              {r.notes && <div className="sourdine rdv-notes">{r.notes}</div>}
                              <div className="rdv-actions">
                                {r.statut === 'prevu' ? (
                                  <>
                                    {/* Le client est là, il dépose : on ouvre le contrat
                                        sans le rechercher. Sans fiche rattachée, on part
                                        quand même, avec son nom et son téléphone
                                        pré-remplis à l'étape de création. */}
                                    <button
                                      type="button"
                                      className="btn btn-fantome"
                                      onClick={() =>
                                        naviguer('/depot', {
                                          state: r.deposantId
                                            ? { deposant: { id: r.deposantId } as Deposant, rdvId: r.id }
                                            : { nouveauClient: { nom: r.nom, gsm: r.tel }, rdvId: r.id },
                                        })
                                      }
                                      title="Ouvrir un contrat de dépôt pour ce rendez-vous"
                                    >
                                      Déposer
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-fantome"
                                      onClick={() => void changerStatut(r, 'honore')}
                                    >
                                      Venu
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-fantome"
                                      onClick={() => void changerStatut(r, 'absent')}
                                    >
                                      Absent
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    type="button"
                                    className="btn btn-fantome"
                                    onClick={() => void changerStatut(r, 'prevu')}
                                  >
                                    Rétablir
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="btn btn-fantome"
                                  onClick={() => setEnEdition(r)}
                                  aria-label={`Modifier le rendez-vous de ${r.nom}`}
                                >
                                  Modifier
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-fantome"
                                  onClick={() => void supprimer(r)}
                                  aria-label={`Supprimer le rendez-vous de ${r.nom}`}
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}

                {ouvertAuDepot &&
                  (ouvert === cle ? (
                    <NouveauRdv
                      jour={cle}
                      heureInitiale={heureOuverture}
                      onAnnuler={() => {
                        setOuvert(null)
                        setHeureOuverture(undefined)
                      }}
                      onCree={() => {
                        setOuvert(null)
                        setHeureOuverture(undefined)
                        charger()
                        signaler('Rendez-vous enregistré.')
                      }}
                      onErreur={alerter}
                    />
                  ) : (
                    <div className="rdv-creneaux">
                      {CRENEAUX_RAPIDES.map((h) => {
                        const pris = liste.find((r) => r.statut !== 'annule' && heureDe(r.debut) === h)
                        const periode = h < '13:00' ? 'btn-creneau-matin' : 'btn-creneau-apm'
                        return (
                          <button
                            key={h}
                            type="button"
                            className={`btn btn-creneau ${pris ? 'btn-creneau-pris' : `btn-secondaire ${periode}`}`}
                            title={pris ? `${pris.nom} — voir les détails` : undefined}
                            onClick={() => {
                              if (pris) {
                                setEnEdition(pris)
                                return
                              }
                              setHeureOuverture(h)
                              setOuvert(cle)
                            }}
                          >
                            <span className="btn-creneau-heure">{h}</span>
                            {pris && <span className="btn-creneau-nom">{pris.nom}</span>}
                          </button>
                        )
                      })}
                      <button
                        type="button"
                        className="btn btn-secondaire ajouter-rdv"
                        onClick={() => {
                          setHeureOuverture(undefined)
                          setOuvert(cle)
                        }}
                      >
                        + Autre heure
                      </button>
                    </div>
                  ))}

                <NoteDuJour
                  jour={cle}
                  texte={notes[cle] ?? ''}
                  onEnregistre={(texte) => setNotes((n) => ({ ...n, [cle]: texte }))}
                  onErreur={alerter}
                />
              </div>
            )
          })}
        </div>

        <div className="note" style={{ margin: '20px 24px' }}>
          <strong>Voir ces rendez-vous dans Google Agenda ou Calendrier.</strong>{' '}
          {abonnement ? (
            <>
              Abonnez-vous à cette adresse — Google Agenda : « Autres agendas » → « À partir de
              l’URL ».
              <code className="adresse-abonnement">{abonnement}</code>
              L’abonnement est en <strong>lecture seule</strong> et se rafraîchit tout seul : ce
              qui est saisi ici apparaît sur votre téléphone, mais un rendez-vous créé dans Google
              ne reviendra pas dans l’application. <strong>Gardez cette adresse pour vous</strong> :
              la clé qu’elle contient donne accès aux noms et téléphones de vos clients.
            </>
          ) : (
            'Chargement de l’adresse d’abonnement…'
          )}
        </div>
      </div>
    </Ecran>
  )
}

/** Le formulaire de prise de rendez-vous, replié dans la colonne du jour. */
function NouveauRdv({
  jour,
  heureInitiale,
  existant,
  onAnnuler,
  onCree,
  onErreur,
}: {
  jour: string
  heureInitiale?: string
  existant?: RendezVous
  onAnnuler: () => void
  onCree: () => void
  onErreur: (message: string) => void
}) {
  const [heure, setHeure] = useState(() => {
    if (existant) {
      const d = new Date(existant.debut)
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    }
    return heureInitiale ?? '10:00'
  })
  const [nom, setNom] = useState(existant?.nom ?? '')
  const [tel, setTel] = useState(existant?.tel ?? '')
  const [pieces, setPieces] = useState(existant?.nbPieces ? String(existant.nbPieces) : '')
  const [notes, setNotes] = useState(existant?.notes ?? '')
  const [deposant, setDeposant] = useState<Deposant | null>(null)
  const [pistes, setPistes] = useState<Deposant[]>([])
  const [envoi, setEnvoi] = useState(false)

  // Le nom tapé cherche une fiche existante : rattacher le rendez-vous au bon
  // client évite de recréer une fiche en double le jour du dépôt.
  useEffect(() => {
    if (deposant || nom.trim().length < 3) {
      setPistes([])
      return
    }
    const controleur = new AbortController()
    const minuterie = window.setTimeout(() => {
      api<{ deposants: Deposant[] }>(`/deposants?q=${encodeURIComponent(nom)}`, {
        signal: controleur.signal,
      })
        .then((r) => setPistes(r.deposants.slice(0, 4)))
        .catch(() => undefined)
    }, 250)
    return () => {
      window.clearTimeout(minuterie)
      controleur.abort()
    }
  }, [nom, deposant])

  const nbPieces = Number.parseInt(pieces, 10) || 0
  const alertes = avertissementsRdv(versIso(jour, heure), nbPieces)

  const enregistrer = async () => {
    if (envoi) return
    if (!nom.trim()) return onErreur('Le nom du client est requis.')
    setEnvoi(true)
    try {
      const corps = {
        debut: versIso(jour, heure),
        nom: nom.trim(),
        tel,
        nbPieces,
        notes,
        deposantId: deposant?.id ?? existant?.deposantId ?? null,
      }
      if (existant) {
        await api(`/rendez-vous/${existant.id}`, { methode: 'PUT', corps })
      } else {
        await api('/rendez-vous', { methode: 'POST', corps })
      }
      onCree()
    } catch (cause) {
      onErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.')
    } finally {
      setEnvoi(false)
    }
  }

  return (
    <div className="rdv-formulaire">
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          className="saisie saisie-compacte"
          type="time"
          step={900}
          aria-label="Heure"
          value={heure}
          onChange={(e) => setHeure(e.target.value)}
        />
        <input
          className="saisie saisie-compacte"
          style={{ width: 64 }}
          inputMode="numeric"
          placeholder="pièces"
          aria-label="Nombre de pièces"
          value={pieces}
          onChange={(e) => setPieces(e.target.value.replace(/\D/g, ''))}
        />
      </div>
      <input
        className="saisie saisie-compacte"
        placeholder="Nom du client"
        value={deposant ? deposant.nom : nom}
        onChange={(e) => {
          setDeposant(null)
          setNom(e.target.value)
        }}
      />
      {pistes.length > 0 && (
        <div className="rdv-pistes">
          {pistes.map((d) => (
            <button
              key={d.id}
              type="button"
              className="lien-nu"
              onClick={() => {
                setDeposant(d)
                setNom(d.nom)
                setTel(d.gsm || d.tel)
                setPistes([])
              }}
            >
              {d.nom} · {d.ref}
            </button>
          ))}
        </div>
      )}
      <input
        className="saisie saisie-compacte"
        placeholder="Téléphone"
        value={tel}
        onChange={(e) => setTel(e.target.value)}
      />
      <input
        className="saisie saisie-compacte"
        placeholder="Note (meubles, à porter…)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />

      {alertes.map((alerte) => (
        <div key={alerte} className="rdv-alerte">
          {alerte}
        </div>
      ))}

      <div style={{ display: 'flex', gap: 6 }}>
        <button type="button" className="btn btn-secondaire" onClick={onAnnuler}>
          Annuler
        </button>
        <button
          type="button"
          className="btn btn-primaire"
          style={{ flex: 1 }}
          disabled={envoi || !nom.trim()}
          onClick={() => void enregistrer()}
        >
          {existant ? 'Modifier' : 'Enregistrer'}
        </button>
      </div>
    </div>
  )
}

/**
 * La note libre d'une journée.
 *
 * Ce sont les choses qui ne rentrent dans aucun champ : « livraison camion
 * 14 h », « Camille absente l'après-midi », « fermeture exceptionnelle ».
 * Jusqu'ici elles s'écrivaient sur un post-it collé au bord de l'écran.
 *
 * L'enregistrement se fait à la sortie du champ, sans bouton : une note qu'on
 * doit penser à valider est une note qu'on perd.
 */
function NoteDuJour({
  jour,
  texte,
  onEnregistre,
  onErreur,
}: {
  jour: string
  texte: string
  onEnregistre: (texte: string) => void
  onErreur: (message: string) => void
}) {
  const [brouillon, setBrouillon] = useState(texte)
  const [edition, setEdition] = useState(false)

  // La note peut changer sous nos pieds — changement de semaine, rechargement.
  useEffect(() => setBrouillon(texte), [texte])

  const enregistrer = async () => {
    setEdition(false)
    if (brouillon.trim() === texte.trim()) return
    try {
      await api(`/notes-jour/${jour}`, { methode: 'PUT', corps: { texte: brouillon } })
      onEnregistre(brouillon.trim())
    } catch (cause) {
      onErreur(cause instanceof ErreurApi ? cause.message : 'Note non enregistrée.')
      setBrouillon(texte)
    }
  }

  if (!edition && !texte) {
    return (
      <button type="button" className="note-jour-vide" onClick={() => setEdition(true)}>
        + Note
      </button>
    )
  }

  return edition ? (
    <textarea
      className="saisie note-jour"
      autoFocus
      rows={2}
      placeholder="Note du jour"
      value={brouillon}
      onChange={(e) => setBrouillon(e.target.value)}
      onBlur={() => void enregistrer()}
    />
  ) : (
    <button type="button" className="note-jour-texte" onClick={() => setEdition(true)}>
      {texte}
    </button>
  )
}
